import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconButton from '@mui/material/IconButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';

const headerFormat = 'ddd, D MMM YYYY';

function RangeDay(props) {
  const { day, start, end, ...pickersDayProps } = props;
  
  if (!start && !end) {
    return <PickersDay {...pickersDayProps} day={day} />;
  }

  const d = dayjs(day);
  const startD = start ? dayjs(start) : null;
  const endD = end ? dayjs(end) : null;
  
  const isStart = startD && d.isSame(startD, 'day');
  const isEnd = endD && d.isSame(endD, 'day');
  const isInRange =
    startD &&
    endD &&
    !isStart &&
    !isEnd &&
    d.isAfter(startD, 'day') &&
    d.isBefore(endD, 'day');
  
  const selected = isStart || isEnd || isInRange;
  
  // Custom styles for different range states
  let customSx = {};
  
  if (isInRange) {
    // Days in the middle of the range
    customSx = {
      borderRadius: 0,
      backgroundColor: 'primary.light',
      color: 'primary.contrastText',
      opacity: 0.6,
      '&:hover': {
        backgroundColor: 'primary.main',
        opacity: 0.8,
      },
      '&.Mui-selected': {
        backgroundColor: 'primary.light',
        opacity: 0.6,
      },
    };
  } else if (isStart || isEnd) {
    // Start and end days get full styling
    customSx = {
      fontWeight: 700,
      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.4)',
      '&.Mui-selected': {
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      },
    };
  }

  return (
    <PickersDay
      {...pickersDayProps}
      day={day}
      selected={selected}
      disableMargin={isInRange}
      sx={customSx}
    />
  );
}

export default function DateRangePickerDropdown({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  label = 'Date range',
  placeholder = 'Select date range',
  disabled,
  isDarkMode = false,
  minDate,
  maxDate,
  sx,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentView, setCurrentView] = React.useState('day');
  const [selectionTarget, setSelectionTarget] = React.useState(null);
  const [displayedMonth, setDisplayedMonth] = React.useState(() => 
    endDate ? dayjs(endDate) : startDate ? dayjs(startDate) : dayjs()
  );
  const open = Boolean(anchorEl);

  const startD = startDate ? dayjs(startDate) : null;
  const endD = endDate ? dayjs(endDate) : null;

  const displayValue = React.useMemo(() => {
    if (startD && endD) return `${startD.format(headerFormat)} → ${endD.format(headerFormat)}`;
    if (startD) return `${startD.format(headerFormat)} → (no end date)`;
    if (endD) return `(no start date) → ${endD.format(headerFormat)}`;
    return '';
  }, [startD, endD]);

  const handleOpen = React.useCallback((e) => {
    setAnchorEl(e.currentTarget);
    setCurrentView('day');
    // Set displayed month to the most recent selected date or today
    const initialMonth = endDate ? dayjs(endDate) : startDate ? dayjs(startDate) : dayjs();
    setDisplayedMonth(initialMonth);
  }, [endDate, startDate]);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
    setSelectionTarget(null);
    // Reset to day view after closing (with slight delay for smoother UX)
    setTimeout(() => setCurrentView('day'), 150);
  }, []);

  const handleChange = React.useCallback(
    (date) => {
      // Only process day selections, ignore year/month selections
      if (currentView !== 'day' || !date) {
        return;
      }

      const d = dayjs(date);
      
      // Validate the date is within bounds
      if (minDate && d.isBefore(dayjs(minDate), 'day')) {
        return;
      }
      if (maxDate && d.isAfter(dayjs(maxDate), 'day')) {
        return;
      }

      if (selectionTarget === 'start') {
        onStartChange(d.toDate());
        setDisplayedMonth(d);
        setSelectionTarget(null);
        return;
      }

      if (selectionTarget === 'end') {
        onEndChange(d.toDate());
        setDisplayedMonth(d);
        setSelectionTarget(null);
        return;
      }

      if (!startD && endD) {
        onEndChange(d.toDate());
        setDisplayedMonth(d);
        return;
      }
      
      if (!startD) {
        // First date selection
        onStartChange(d.toDate());
        setDisplayedMonth(d);
        return;
      }
      
      if (!endD) {
        // Second date selected
        if (d.isBefore(startD, 'day')) {
          // If second date is before first, swap them
          onEndChange(startD.toDate());
          onStartChange(d.toDate());
          setDisplayedMonth(d);
        } else if (d.isSame(startD, 'day')) {
          // If clicking the same day, set end to same day and close
          onEndChange(d.toDate());
          setDisplayedMonth(d);
          setTimeout(() => handleClose(), 300);
          return;
        } else {
          onEndChange(d.toDate());
          setDisplayedMonth(d);
        }
        // Close popover after both dates selected for smoother UX
        setTimeout(() => handleClose(), 300);
        return;
      }
      
      // Both dates already selected, start new range
      onStartChange(d.toDate());
      onEndChange(null);
      setDisplayedMonth(d);
    },
    [currentView, startD, endD, selectionTarget, onStartChange, onEndChange, handleClose, minDate, maxDate]
  );

  const handleViewChange = React.useCallback((view) => {
    setCurrentView(view);
  }, []);

  const handleMonthChange = React.useCallback((newMonth) => {
    setDisplayedMonth(dayjs(newMonth));
  }, []);

  const handleYearChange = React.useCallback((newYear) => {
    setDisplayedMonth(dayjs(newYear));
  }, []);

  const textFieldSx = {
    width: '420px',
    minWidth: '320px',
    maxWidth: '100%',
    flexShrink: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
      '& fieldset': {
        borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
        transition: 'border-color 0.2s ease',
      },
      '&:hover': {
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        '& fieldset': {
          borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)',
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.02)',
        boxShadow: `0 0 0 2px ${isDarkMode ? 'rgba(25, 118, 210, 0.4)' : 'rgba(25, 118, 210, 0.25)'}`,
        '& fieldset': {
          borderColor: 'primary.main',
          borderWidth: 1,
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'text.secondary',
      '&.Mui-focused': {
        color: 'primary.main',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '0.9375rem',
      fontWeight: 500,
      color: 'text.primary',
      cursor: 'pointer',
      letterSpacing: '0.01em',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    ...sx,
  };

  return (
    <>
      <TextField
        label={label}
        value={displayValue}
        placeholder={placeholder}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen(e);
          }
        }}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <CalendarMonthIcon sx={{ color: 'primary.main', fontSize: 22, opacity: 0.9 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <KeyboardArrowDownIcon
                sx={{
                  color: 'text.secondary',
                  fontSize: 20,
                  transition: 'transform 0.2s ease',
                  transform: open ? 'rotate(180deg)' : 'none',
                }}
              />
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        sx={textFieldSx}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionProps={{
          timeout: 250,
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2.5,
              p: 2.5,
              minWidth: 336,
              boxShadow: isDarkMode
                ? '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)'
                : '0 12px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
                py: 2,
                px: 1.5,
                borderRadius: 1.5,
                backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.06)',
                border: `1px solid ${isDarkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.15)'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={() => setSelectionTarget('start')}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    color: selectionTarget === 'start' || startD ? 'primary.main' : 'text.secondary',
                    textDecoration: startD ? 'underline' : 'none',
                    textUnderlineOffset: 3,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    p: 0,
                  }}
                >
                  {startD ? startD.format(headerFormat) : 'From'}
                </Typography>
                {startD && (
                  <IconButton
                    size="small"
                    aria-label="Clear since date"
                    onClick={() => onStartChange(null)}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
              <Typography
                component="span"
                variant="body1"
                sx={{
                  color: 'primary.main',
                  opacity: 0.8,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                →
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={() => setSelectionTarget('end')}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    color: selectionTarget === 'end' || endD ? 'primary.main' : 'text.secondary',
                    textDecoration: endD ? 'underline' : 'none',
                    textUnderlineOffset: 3,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    p: 0,
                  }}
                >
                  {endD ? endD.format(headerFormat) : 'To'}
                </Typography>
                {endD && (
                  <IconButton
                    size="small"
                    aria-label="Clear until date"
                    onClick={() => onEndChange(null)}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
            <DateCalendar
              onChange={handleChange}
              onViewChange={handleViewChange}
              onMonthChange={handleMonthChange}
              onYearChange={handleYearChange}
              minDate={minDate ? dayjs(minDate) : undefined}
              maxDate={maxDate ? dayjs(maxDate) : undefined}
              referenceDate={displayedMonth}
              views={['year', 'month', 'day']}
              openTo="day"
              reduceAnimations={false}
              slots={{ day: RangeDay }}
              slotProps={{
                day: {
                  start: startD?.toDate?.() ?? startDate,
                  end: endD?.toDate?.() ?? endDate,
                },
              }}
              sx={{
                '& .MuiPickersCalendarHeader-root': {
                  marginBottom: 1,
                },
                '& .MuiPickersCalendarHeader-label': {
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    opacity: 0.7,
                  },
                },
                '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                },
                '& .MuiDayCalendar-weekContainer': {
                  margin: '2px 0',
                },
                '& .MuiPickersDay-root': {
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                },
              }}
            />
          </Box>
        </LocalizationProvider>
      </Popover>
    </>
  );
}

export { headerFormat };
